import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput, getDateTimeStr, formatEventTime } from "./shared/utils";

interface CalendarEvent {
  summary?: string;
  title?: string;
  start?: string | { dateTime?: string; date?: string };
  end?: string | { dateTime?: string; date?: string };
  location?: string;
  id?: string;
}

interface ParsedEventList {
  ok: true;
  events: CalendarEvent[];
  date?: string;
  calendar?: string;
}

function calcDuration(
  start: string | { dateTime?: string; date?: string } | undefined,
  end: string | { dateTime?: string; date?: string } | undefined,
): string {
  const s = getDateTimeStr(start);
  const e = getDateTimeStr(end);
  if (!s || !e) return "";
  try {
    const diff = new Date(e).getTime() - new Date(s).getTime();
    if (isNaN(diff) || diff <= 0) return "";
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  } catch {
    return "";
  }
}

function formatDateHeader(dateStr: string | undefined): string {
  if (!dateStr) return "Events";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  } catch {
    return dateStr;
  }
}

interface CalendarListCardProps {
  part: ToolUIPart;
}

export const CalendarListCard = memo(function CalendarListCard({ part }: CalendarListCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedEventList>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const events = result.kind === "ok" ? result.data.events : [];
  const dateStr = result.kind === "ok" ? result.data.date : undefined;
  const title = dateStr ? formatDateHeader(dateStr) : "Calendar";

  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const badge = isStreaming ? undefined : isError ? "error" : `${events.length} event${events.length !== 1 ? "s" : ""}`;

  return (
    <CardShell
      icon="◈"
      title={title}
      badge={badge}
      accent={isError ? "error" : "calendar"}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Loading calendar...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.kind === "error" ? result.error : "Error"}</div>
      )}
      {!isStreaming && !isError && events.length === 0 && (
        <div className="tool-card__empty">No events found.</div>
      )}
      {!isStreaming && !isError && events.length > 0 && (
        <div role="list">
          {events.map((event, i) => {
            const summary = event.summary ?? event.title ?? "(untitled)";
            const startTime = formatEventTime(event.start);
            const duration = calcDuration(event.start, event.end);

            return (
              <div key={event.id ?? i} className="tool-card__cal-event" role="listitem">
                <span className="tool-card__cal-time">{startTime}</span>
                <span className="tool-card__cal-divider" aria-hidden="true" />
                <span className="tool-card__cal-details">
                  <span className="tool-card__cal-title">{summary}</span>
                  {duration && (
                    <span className="tool-card__cal-duration">{duration}</span>
                  )}
                  {event.location && (
                    <span className="tool-card__cal-location">{event.location}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
});
